import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import EditGodMemberForm from '../components/EditGodMemberForm';
import MemberDetailsModal from '../components/MemberDetailsModal';
import GodViewMediaModal from '../components/GodViewMediaModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiImage, FiEye, FiEdit2, FiTrash2, FiSearch, FiCopy, FiPlus, FiX } from 'react-icons/fi';

const POPULATE = 'populate=godTGs,payments,handlerPayments,participantPayments,shop,lastGodGroup';

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true',
});

function normalizeSbfIds(ids) {
    return (ids || []).map((s) => (s != null ? String(s).trim() : '')).filter(Boolean);
}

const SBFNeedReviewPage = () => {
    const [membersNewSetupDone, setMembersNewSetupDone] = useState([]);
    const [membersNewSetupPending, setMembersNewSetupPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pageNewSetupDone, setPageNewSetupDone] = useState(1);
    const [pageNewSetupPending, setPageNewSetupPending] = useState(1);
    const [totalPagesNewSetupDone, setTotalPagesNewSetupDone] = useState(1);
    const [totalPagesNewSetupPending, setTotalPagesNewSetupPending] = useState(1);
    const [totalNewSetupDone, setTotalNewSetupDone] = useState(0);
    const [totalNewSetupPending, setTotalNewSetupPending] = useState(0);
    const [selectedMember, setSelectedMember] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryText, setSummaryText] = useState('');
    const [summaryCopied, setSummaryCopied] = useState(false);
    const [sbfDrafts, setSbfDrafts] = useState({});
    const [savingSbf, setSavingSbf] = useState({});
    const viewModalFetchedIdRef = useRef(null);

    const buildListUrl = (page, newSetupDoneFlag, search = '', status = '', sort = '') => {
        const statusParam = status && ['active', 'hold', 'expiring', 'expired', 'cancelled', 'changedName', 'stop'].includes(status) ? `&status=${encodeURIComponent(status)}` : '';
        const sortParam = sort && ['renewalDate', '-renewalDate'].includes(sort) ? `&sort=${encodeURIComponent(sort)}` : '';
        const nsd = newSetupDoneFlag ? '&newSetupDone=true' : '&newSetupDone=false';
        return `${process.env.REACT_APP_API_URL}/api/godmembers?page=${page}&limit=20&sbfNeedReview=true${nsd}&${POPULATE}${search ? `&search=${encodeURIComponent(search)}` : ''}${statusParam}${sortParam}`;
    };

    const fetchBothSections = async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const headers = authHeaders();
            const [resDone, resPending] = await Promise.all([
                axios.get(buildListUrl(pageNewSetupDone, true, searchTerm, statusFilter, sortBy), { headers }),
                axios.get(buildListUrl(pageNewSetupPending, false, searchTerm, statusFilter, sortBy), { headers }),
            ]);

            if (resDone.data?.success) {
                setMembersNewSetupDone(resDone.data.data || []);
                setTotalPagesNewSetupDone(resDone.data.totalPages || 1);
                setTotalNewSetupDone(resDone.data.total ?? 0);
            }
            if (resPending.data?.success) {
                setMembersNewSetupPending(resPending.data.data || []);
                setTotalPagesNewSetupPending(resPending.data.totalPages || 1);
                setTotalNewSetupPending(resPending.data.total ?? 0);
            }
        } catch (err) {
            setError(`Failed to fetch members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBothSections();
    }, [pageNewSetupDone, pageNewSetupPending, searchTerm, statusFilter, sortBy]);

    useEffect(() => {
        if (!showViewModal || !selectedMember?._id) return;
        const memberId = selectedMember._id;
        if (viewModalFetchedIdRef.current === memberId) return;
        viewModalFetchedIdRef.current = memberId;

        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
        const baseUrl = `${process.env.REACT_APP_API_URL}/api/godmembers/${memberId}`;

        axios.get(`${baseUrl}?view=summary`, { headers })
            .then((res) => {
                if (res.data.success) setSelectedMember(res.data.data);
            })
            .catch((err) => {
                setError(`Failed to fetch member details: ${err.response?.data?.error || err.message}`);
            })
            .finally(() => setViewDetailsLoading(false));

        axios.get(baseUrl, { headers })
            .then((res) => {
                if (res.data.success) {
                    setSelectedMember((prev) => (prev && prev._id === memberId ? res.data.data : prev));
                }
            })
            .catch(() => {});
    }, [showViewModal, selectedMember?._id]);

    const patchSbfFields = async (member, payload) => {
        const res = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/godmembers/${member._id}`,
            payload,
            { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
        );
        if (!res.data?.success) {
            throw new Error(res.data?.error || 'Failed to save');
        }
        return res.data.data;
    };

    const getDraftIds = (member) => {
        if (Object.prototype.hasOwnProperty.call(sbfDrafts, member._id)) {
            const draft = sbfDrafts[member._id];
            return draft.length ? draft : [''];
        }
        return member.sbfID?.length ? [...member.sbfID] : [''];
    };

    const saveSbfIds = async (member) => {
        if (savingSbf[member._id]) return;
        const nextIds = normalizeSbfIds(getDraftIds(member));
        const prevIds = normalizeSbfIds(member.sbfID);
        const unchanged =
            nextIds.length === prevIds.length && nextIds.every((id, i) => id === prevIds[i]);
        if (unchanged) {
            setSbfDrafts((prev) => {
                if (!Object.prototype.hasOwnProperty.call(prev, member._id)) return prev;
                const { [member._id]: _, ...rest } = prev;
                return rest;
            });
            return;
        }
        try {
            setSavingSbf((prev) => ({ ...prev, [member._id]: true }));
            setError('');
            await patchSbfFields(member, { sbfID: nextIds });
            setSbfDrafts((prev) => {
                const { [member._id]: _, ...rest } = prev;
                return rest;
            });
            await fetchBothSections(false);
        } catch (err) {
            setError(`Failed to save SBF ID: ${err.response?.data?.error || err.message}`);
        } finally {
            setSavingSbf((prev) => ({ ...prev, [member._id]: false }));
        }
    };

    const saveNeedReview = async (member, nextValue) => {
        if (savingSbf[member._id]) return;
        if (!!member.sbfNeedReview === !!nextValue) return;
        try {
            setSavingSbf((prev) => ({ ...prev, [member._id]: true }));
            setError('');
            await patchSbfFields(member, { sbfNeedReview: !!nextValue });
            await fetchBothSections(false);
        } catch (err) {
            setError(`Failed to save Need Review: ${err.response?.data?.error || err.message}`);
        } finally {
            setSavingSbf((prev) => ({ ...prev, [member._id]: false }));
        }
    };

    const handleSbfIdsBlur = (event, member) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        saveSbfIds(member);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        setSelectedMember(null);
        fetchBothSections();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const handleView = (member) => {
        setSelectedMember(member);
        setShowViewModal(true);
        setViewDetailsLoading(true);
    };

    const handleEdit = async (member) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers/${member._id}?view=edit`,
                { headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' } }
            );
            if (response.data.success) {
                setSelectedMember(response.data.data);
                setShowEditModal(true);
            }
        } catch (err) {
            setError(`Failed to fetch member for edit: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = (member) => {
        setSelectedMember(member);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/godmembers/${selectedMember._id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setShowDeleteConfirm(false);
            setSelectedMember(null);
            fetchBothSections();
        } catch (err) {
            setError(`Failed to delete member: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setPageNewSetupDone(1);
        setPageNewSetupPending(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setStatusFilter('');
        setSortBy('');
        setPageNewSetupDone(1);
        setPageNewSetupPending(1);
    };

    const handleShowSummary = async () => {
        setShowSummaryModal(true);
        setSummaryLoading(true);
        setSummaryText('');
        setSummaryCopied(false);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers?page=1&limit=500&sbfNeedReview=true&newSetupDone=true`,
                { headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' } }
            );
            const list = res.data?.success ? res.data.data || [] : [];
            const repostLines = [];
            const addCandidates = [];
            for (const m of list) {
                const reviewReason = String(m.sbfReviewReason || '').trim();
                const isRepost = m.sbfID?.length > 0 && (reviewReason === 'New Payment' || !reviewReason);
                const godName = m.godName || 'N/A';
                if (isRepost) {
                    for (const id of m.sbfID) {
                        if (id && String(id).trim()) repostLines.push(`${String(id).trim()} - ${godName}`);
                    }
                } else {
                    const hasWebsite = m.website && String(m.website).trim();
                    const godID = m.godID || 'N/A';
                    const webID = m.webID || 'N/A';
                    const line = hasWebsite ? String(m.website).trim() : `${godID} - ${godName} - ${webID} - No website`;
                    const renewalDateValue = m.renewalDate ? new Date(m.renewalDate).getTime() : Number.POSITIVE_INFINITY;
                    addCandidates.push({ line, renewalDateValue });
                }
            }
            const addLines = addCandidates
                .sort((a, b) => a.renewalDateValue - b.renewalDateValue)
                .map((item) => item.line);
            const parts = [];
            if (repostLines.length) {
                parts.push('REPOST SBF');
                parts.push(repostLines.join('\n'));
            }
            if (addLines.length) {
                if (parts.length) parts.push('');
                parts.push('ADD SBF');
                parts.push(addLines.join('\n'));
            }
            setSummaryText(parts.length ? parts.join('\n') : 'No members with new setup done needing SBF review.');
        } catch (err) {
            setSummaryText(`Failed to load summary: ${err.response?.data?.error || err.message}`);
        } finally {
            setSummaryLoading(false);
        }
    };

    const handleCopySummary = () => {
        if (!summaryText) return;
        navigator.clipboard.writeText(summaryText).then(() => {
            setSummaryCopied(true);
            setTimeout(() => setSummaryCopied(false), 2000);
        });
    };

    const renderMemberRows = (list, emptyMessage) => {
        if (loading) {
            return (
                <tr><td colSpan="7" className="px-6 py-4 text-center">Loading...</td></tr>
            );
        }
        if (!list.length) {
            return (
                <tr><td colSpan="7" className="px-6 py-4 text-center">{emptyMessage}</td></tr>
            );
        }
        return list.map((member) => (
            <tr key={member._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm">{member.fullName}</td>
                <td className="px-6 py-4 text-sm">
                    <div
                        className="flex flex-col gap-1 min-w-[9rem]"
                        onBlur={(e) => handleSbfIdsBlur(e, member)}
                    >
                        {getDraftIds(member).map((id, index) => (
                            <div key={`${member._id}-sbf-${index}`} className="flex items-center gap-0.5">
                                <input
                                    type="text"
                                    value={id}
                                    disabled={!!savingSbf[member._id]}
                                    placeholder="SBF ID"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSbfDrafts((prev) => {
                                            const current = Object.prototype.hasOwnProperty.call(prev, member._id)
                                                ? prev[member._id]
                                                : (member.sbfID?.length ? [...member.sbfID] : ['']);
                                            const next = current.map((v, i) => (i === index ? value : v));
                                            return { ...prev, [member._id]: next };
                                        });
                                    }}
                                    className="w-full min-w-0 rounded border border-gray-300 py-1 px-1.5 text-sm text-blue-700 font-bold focus:border-blue-500 focus:ring-blue-500"
                                />
                                {getDraftIds(member).length > 1 && (
                                    <button
                                        type="button"
                                        disabled={!!savingSbf[member._id]}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setSbfDrafts((prev) => {
                                                const current = Object.prototype.hasOwnProperty.call(prev, member._id)
                                                    ? prev[member._id]
                                                    : (member.sbfID?.length ? [...member.sbfID] : ['']);
                                                const next = current.filter((_, i) => i !== index);
                                                return { ...prev, [member._id]: next.length ? next : [''] };
                                            });
                                        }}
                                        className="shrink-0 p-1 rounded border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-600"
                                        title="Remove SBF ID"
                                        aria-label="Remove SBF ID"
                                    >
                                        <FiX className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            disabled={!!savingSbf[member._id]}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                setSbfDrafts((prev) => {
                                    const current = Object.prototype.hasOwnProperty.call(prev, member._id)
                                        ? prev[member._id]
                                        : (member.sbfID?.length ? [...member.sbfID] : ['']);
                                    return { ...prev, [member._id]: [...current, ''] };
                                });
                            }}
                            className="self-start p-1 rounded border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-600"
                            title="Add SBF ID"
                            aria-label="Add SBF ID"
                        >
                            <FiPlus className="w-4 h-4" />
                        </button>
                    </div>
                </td>
                <td className="px-6 py-4 text-sm">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!member.sbfNeedReview}
                            disabled={!!savingSbf[member._id] || !!member.noWebsite}
                            onChange={(e) => saveNeedReview(member, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-600">Need review</span>
                    </label>
                </td>
                <td className="px-6 py-4 text-sm max-w-xs truncate" title={member.sbfReviewReason || ''}>{member.sbfReviewReason || '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.status === 'active' ? 'bg-green-100 text-green-800' : member.status === 'expired' ? 'bg-red-100 text-red-800' : member.status === 'hold' ? 'bg-yellow-100 text-yellow-800' : member.status === 'stop' ? 'bg-rose-100 text-rose-800' : member.status === 'expiring' ? 'bg-orange-100 text-orange-800' : member.status === 'cancelled' ? 'bg-gray-100 text-gray-800' : member.status === 'changedName' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                        {member.status}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(member.renewalDate)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button onClick={() => { setSelectedMember(member); setShowMediaModal(true); }} className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full inline-flex items-center" title="View Media"><FiImage className="w-4 h-4" /></button>
                    <button onClick={() => handleView(member)} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details"><FiEye className="w-4 h-4" /></button>
                    <button onClick={() => handleEdit(member)} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Member"><FiEdit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(member)} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Member"><FiTrash2 className="w-4 h-4" /></button>
                </td>
            </tr>
        ));
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">SBF Need Review</h1>
                        <p className="text-gray-600">
                            Members needing SBF ID review: {totalNewSetupDone + totalNewSetupPending}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                            New setup done: {totalNewSetupDone} · New setup not done: {totalNewSetupPending}
                        </p>
                    </div>
                </div>

                <div className="mb-3 flex flex-nowrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[180px] max-w-[400px]">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                            placeholder="Search: name, God ID, Web ID..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                    <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center shrink-0">
                        <FiSearch className="mr-2" /> Search
                    </button>
                    <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 shrink-0">Clear</button>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPageNewSetupDone(1);
                            setPageNewSetupPending(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="hold">Hold</option>
                        <option value="stop">Stop</option>
                        <option value="expiring">Expiring</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="changedName">Changed Name</option>
                    </select>
                    <select
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setPageNewSetupDone(1);
                            setPageNewSetupPending(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                    >
                        <option value="">Sort: Default</option>
                        <option value="-renewalDate">Sort: Renewal (newest)</option>
                        <option value="renewalDate">Sort: Renewal (oldest)</option>
                    </select>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="mb-10">
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                        <h2 className="text-xl font-semibold text-gray-900">1 — New setup done</h2>
                        <button
                            type="button"
                            onClick={handleShowSummary}
                            className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 flex items-center shrink-0"
                        >
                            Show Summary
                        </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Count: {totalNewSetupDone}</p>
                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SBF ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Need Review</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">sbfReviewReason</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Renewal Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {renderMemberRows(membersNewSetupDone, 'No members in this section')}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={pageNewSetupDone} totalPages={totalPagesNewSetupDone} onPageChange={setPageNewSetupDone} />
                </div>

                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">2 — New setup not done</h2>
                    <p className="text-sm text-gray-600 mb-2">Count: {totalNewSetupPending}</p>
                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SBF ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Need Review</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">sbfReviewReason</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Renewal Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {renderMemberRows(membersNewSetupPending, 'No members in this section')}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={pageNewSetupPending} totalPages={totalPagesNewSetupPending} onPageChange={setPageNewSetupPending} />
                </div>

                {showMediaModal && selectedMember && (
                    <GodViewMediaModal
                        member={selectedMember}
                        onClose={() => setShowMediaModal(false)}
                        onMediaUpdate={() => fetchBothSections()}
                    />
                )}

                {showViewModal && selectedMember && (
                    <MemberDetailsModal
                        member={selectedMember}
                        detailsLoading={viewDetailsLoading}
                        onClose={() => { setShowViewModal(false); setSelectedMember(null); setViewDetailsLoading(false); viewModalFetchedIdRef.current = null; }}
                    />
                )}

                {showDeleteConfirm && selectedMember && (
                    <DeleteConfirmationModal itemName={selectedMember.godName} onConfirm={confirmDelete} onCancel={() => setShowDeleteConfirm(false)} />
                )}

                {showEditModal && selectedMember && (
                    <EditGodMemberForm
                        member={selectedMember}
                        onSuccess={handleEditSuccess}
                        onCancel={() => { setShowEditModal(false); setSelectedMember(null); }}
                    />
                )}

                {showSummaryModal && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
                        <div className="flex min-h-screen items-center justify-center p-4">
                            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSummaryModal(false)} aria-hidden="true" />
                            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">SBF Summary</h2>
                                    <div className="flex items-center gap-2">
                                        {summaryText && (
                                            <>
                                                <button
                                                    onClick={handleCopySummary}
                                                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-1"
                                                >
                                                    <FiCopy className="w-4 h-4" /> Copy
                                                </button>
                                                {summaryCopied && (
                                                    <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">Copied</span>
                                                )}
                                            </>
                                        )}
                                        <button
                                            onClick={() => setShowSummaryModal(false)}
                                            className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                                <div className="px-6 py-4 overflow-auto flex-1 min-h-0">
                                    {summaryLoading ? (
                                        <p className="text-gray-500">Loading summary...</p>
                                    ) : (
                                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans break-all">{summaryText || '—'}</pre>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SBFNeedReviewPage;
